package com.tregz.polyglot

import com.tregz.polyglot.ai.PolyglotTranslator
import com.tregz.polyglot.ai.PolyglotValidator
import com.tregz.polyglot.di.language
import com.tregz.polyglot.io.cloud.PolyglotBundle
import com.tregz.polyglot.io.cloud.PolyglotCache
import com.tregz.polyglot.io.cloud.PolyglotCacheResult
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.time.Duration.Companion.minutes
import kotlin.time.TimeMark
import kotlin.time.TimeSource

class PolyglotManager(
    private val collectionPath: String = "strings",
    private val documentPrefix: String = "",
    private val version: Int,
    private val defaultStrings: Map<String, String>,
    private val translator: PolyglotTranslator = PolyglotTranslator.default,
    private val cache: PolyglotCache = PolyglotCache.default,
    scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
) {
    // Child scope so close() cancels our work without cancelling an injected parent scope.
    private val scope = CoroutineScope(scope.coroutineContext + SupervisorJob(scope.coroutineContext[Job]))

    private val _strings = MutableStateFlow(defaultStrings)
    val strings: StateFlow<Map<String, String>> = _strings.asStateFlow()

    private val loadMutex = Mutex()
    private var requestedLanguage: String? = null
    private var loadedLanguage: String? = null
    private val failures = mutableMapOf<String, TimeMark>()

    init {
        val lang = safeLanguage()
        println("[Polyglot] Initializing for detected language: '$lang'")
        if (lang != "en" && lang.isNotBlank()) {
            this.scope.launch {
                loadTranslations(lang)
            }
        }
    }

    private fun safeLanguage(): String = try {
        language.code
    } catch (ex: CancellationException) {
        throw ex
    } catch (_: Throwable) {
        ""
    }

    /** Cancels pending and future background work (translation, cache upload). */
    fun close() {
        scope.cancel()
    }

    suspend fun loadTranslations(langCode: String) {
        requestedLanguage = langCode
        loadMutex.withLock {
            if (loadedLanguage == langCode) return
            if (requestedLanguage != langCode) return // superseded by a newer request
            val failedAt = failures[langCode]
            if (failedAt != null && failedAt.elapsedNow() < FAILURE_BACKOFF) {
                println("[Polyglot] Skipping '$langCode': recent failure, backing off")
                return
            }
            val docId = "$documentPrefix$langCode"
            println("[Polyglot] Checking cache at '$collectionPath/$docId' (version $version)...")
            when (val result = cache.lookup(collectionPath, docId, version)) {
                is PolyglotCacheResult.Hit -> {
                    val valid = PolyglotValidator.sanitize(defaultStrings, result.strings)
                    if (PolyglotValidator.isAcceptable(defaultStrings, valid)) {
                        println("[Polyglot] Cache hit for '$docId' with ${valid.size} strings")
                        apply(langCode, valid)
                        failures.remove(langCode)
                        return
                    }
                    println("[Polyglot] Cached '$docId' failed validation; re-translating")
                    translateAndStore(langCode, docId)
                }
                is PolyglotCacheResult.Miss -> translateAndStore(langCode, docId)
                is PolyglotCacheResult.Error -> {
                    println("[Polyglot] Cache error for '$docId': ${result.message}. Using default strings.")
                    failures[langCode] = TimeSource.Monotonic.markNow()
                }
            }
        }
    }

    private suspend fun translateAndStore(langCode: String, docId: String) {
        println("[Polyglot] Cache miss for '$docId'. Requesting translation via Gemini...")
        val translated = translator.translate(defaultStrings, langCode)
            ?.let { PolyglotValidator.sanitize(defaultStrings, it) }
        if (translated != null && PolyglotValidator.isAcceptable(defaultStrings, translated)) {
            println("[Polyglot] Gemini translation succeeded for '$docId' (${translated.size} strings)")
            apply(langCode, translated)
            failures.remove(langCode)
            scope.launch {
                cache.upload(collectionPath, docId, PolyglotBundle(version, translated))
            }
        } else {
            println("[Polyglot] Translation failed for '$docId'. Using default strings.")
            failures[langCode] = TimeSource.Monotonic.markNow()
        }
    }

    private fun apply(langCode: String, strings: Map<String, String>) {
        if (requestedLanguage != langCode) return // a newer language was requested meanwhile
        _strings.value = defaultStrings + strings
        loadedLanguage = langCode
    }

    fun get(key: String): String = _strings.value[key] ?: defaultStrings[key] ?: key

    private companion object {
        val FAILURE_BACKOFF = 5.minutes
    }
}
